<template>
	<BaseDrawer
		:show.sync="isShow"
		:title="isEditMode ? 'Edit university' : 'Create university'"
		size="md"
		data-test-id="drawer-uniday-university"
		@close="close"
	>
		<template v-if="isShow">
			<CRow>
				<CCol lg="12">
					<CInput
						v-model.trim="$v.form.customerId.$model"
						:is-valid="isValidCustomerId"
						:invalid-feedback="customerIdErrorMessage"
						:disabled="isEditMode"
						:description="isEditMode
							? 'แก้ไขไม่ได้ — เป็น key ของการ import และ order เดิมอ้างถึงอยู่'
							: 'Apple Customer ID · ตัวเลขเท่านั้น'"
						label="Customer ID*"
						data-test-id="input-customer-id"
						@input="clearServerError('customer_id')"
					/>
				</CCol>
				<CCol lg="12">
					<CInput
						v-model.trim="$v.form.companyEn.$model"
						:is-valid="fieldValid('companyEn', 'company_en')"
						:invalid-feedback="serverErrors.company_en || requiredOrMaxMessage($v.form.companyEn)"
						label="Company EN*"
						data-test-id="input-company-en"
					/>
				</CCol>
				<CCol lg="12">
					<CInput
						v-model.trim="$v.form.companyTh.$model"
						:is-valid="fieldValid('companyTh', 'company_th')"
						:invalid-feedback="serverErrors.company_th || requiredOrMaxMessage($v.form.companyTh)"
						label="Company TH*"
						data-test-id="input-company-th"
					/>
				</CCol>
				<CCol md="6">
					<CInput
						v-model.trim="$v.form.city.$model"
						:is-valid="fieldValid('city', 'city')"
						:invalid-feedback="serverErrors.city || maxLengthMessage"
						label="City"
					/>
				</CCol>
				<CCol md="6">
					<CInput
						v-model.trim="$v.form.state.$model"
						:is-valid="fieldValid('state', 'state')"
						:invalid-feedback="serverErrors.state || maxLengthMessage"
						label="State"
					/>
				</CCol>
				<CCol lg="12">
					<CInput
						v-model.trim="$v.form.organizeId.$model"
						:is-valid="fieldValid('organizeId', 'organize_id')"
						:invalid-feedback="serverErrors.organize_id || organizeIdMaxLengthMessage"
						label="DEPID / Organize ID"
					/>
				</CCol>
				<CCol lg="12" class="toggle-group mb-3">
					<div class="d-flex align-items-center">
						<label class="toggle-label mb-0 mr-3" for="uniday-university-status">Status</label>
						<CSwitch
							id="uniday-university-status"
							:checked="isActive"
							variant="3d"
							size="sm"
							class="switch-custom toggle-status"
							color="success"
							data-test-id="switch-status"
							@update:checked="handleUpdateStatus"
						/>
						<span class="ml-2">{{ isActive ? 'Active' : 'Inactive' }}</span>
					</div>
					<small class="form-text text-muted">
						เปิด = แสดงใน dropdown หน้า Ecom / ปิด = ซ่อน
						· <strong>ลบไม่ได้</strong> เพราะ order เดิมยังอ้างถึงอยู่ ใช้การปิดแทน
					</small>
				</CCol>
			</CRow>
		</template>
		<template #footer>
			<div class="d-flex justify-content-end w-100">
				<CButton
					:disabled="isSubmitting"
					class="transparent"
					color="tertiary"
					@click="close"
				>
					Cancel
				</CButton>
				<CButton
					:disabled="isSubmitting"
					class="ml-3"
					color="primary"
					data-test-id="submit-button"
					@click="handleSubmit"
				>
					<template v-if="isSubmitting">{{ isEditMode ? 'Saving' : 'Creating' }}</template>
					<template v-else>{{ isEditMode ? 'Save changes' : 'Create' }}</template>
				</CButton>
			</div>
		</template>
	</BaseDrawer>
</template>

<script>
import { required, numeric, maxLength } from 'vuelidate/lib/validators';
import BaseDrawer from '@/components/BaseDrawer.vue';
import {
	UNIDAY_UNIVERSITY_STATUSES,
	UNIDAY_UNIVERSITY_ORGANIZE_ID_MAX_LENGTH,
} from '../enums/unidayUniversities';
import {
	transformedUnidayUniversityForm,
	transformedUnidayUniversityToAPI,
} from '../assets/js/transform/unidayUniversities';
import { pathOr } from '../assets/js/helpers';

const MAX_LENGTH = 255;

/**
 * Drawer สร้าง / แก้ไข University — ใช้ตัวเดียวทั้ง 2 โหมด
 * เปิดแบบ imperative ตามแพตเทิร์น drawer ของโปรเจกต์: `$refs[...].open(formData?)`
 * ไม่ส่ง formData = create · ส่ง (จาก getter `getUnidayUniversityFormById`) = edit
 *
 * emit `onSubmit({ id, params, isEditMode })` แล้วรอ parent เรียก `close()` / `setServerErrors(error)`
 * ไม่มีปุ่มลบโดยตั้งใจ — order เดิมอ้างถึง university อยู่ ใช้ status = 0 แทน
 */
export default {
	name: 'DrawerUnidayUniversity',

	components: {
		BaseDrawer,
	},

	props: {
		isSubmitting: {
			type: Boolean,
			default: false,
		},
	},

	validations() {
		return {
			form: {
				// edit แก้ customer_id ไม่ได้ → ไม่ต้อง validate (กันข้อมูลเก่าที่อาจไม่ใช่ตัวเลขล็อกฟอร์ม)
				customerId: this.isEditMode ? {} : { required, numeric },
				companyEn: { required, maxLength: maxLength(MAX_LENGTH) },
				companyTh: { required, maxLength: maxLength(MAX_LENGTH) },
				city: { maxLength: maxLength(MAX_LENGTH) },
				state: { maxLength: maxLength(MAX_LENGTH) },
				organizeId: { maxLength: maxLength(UNIDAY_UNIVERSITY_ORGANIZE_ID_MAX_LENGTH) },
			},
		};
	},

	data() {
		return {
			isShow: false,
			form: transformedUnidayUniversityForm(),
			// 422 errors ของ API แยกตาม field (key snake_case ตามที่ BE ตอบ) → ข้อความแรกของแต่ละ field
			serverErrors: {},
		};
	},

	computed: {
		isEditMode() {
			return !!this.form.id;
		},

		isActive() {
			return this.form.status === UNIDAY_UNIVERSITY_STATUSES.ACTIVE;
		},

		isValidCustomerId() {
			return this.fieldValid('customerId', 'customer_id');
		},

		customerIdErrorMessage() {
			if (this.serverErrors.customer_id) {
				return this.serverErrors.customer_id;
			}
			if (!this.$v.form.customerId.required) {
				return this.$t('global.error.required');
			}
			return 'Customer ID ต้องเป็นตัวเลขเท่านั้น';
		},

		maxLengthMessage() {
			return `ไม่เกิน ${MAX_LENGTH} ตัวอักษร`;
		},

		organizeIdMaxLengthMessage() {
			return `ไม่เกิน ${UNIDAY_UNIVERSITY_ORGANIZE_ID_MAX_LENGTH} ตัวอักษร`;
		},
	},

	methods: {
		open(formData = null) {
			this.form = formData
				? { ...formData }
				: transformedUnidayUniversityForm({ status: UNIDAY_UNIVERSITY_STATUSES.ACTIVE });
			this.serverErrors = {};
			this.$v.$reset();
			this.isShow = true;
		},

		close() {
			this.isShow = false;
		},

		requiredOrMaxMessage(field) {
			return field.required ? this.maxLengthMessage : this.$t('global.error.required');
		},

		handleUpdateStatus(checked) {
			this.form.status = checked
				? UNIDAY_UNIVERSITY_STATUSES.ACTIVE
				: UNIDAY_UNIVERSITY_STATUSES.INACTIVE;
		},

		fieldValid(formKey, apiKey) {
			if (this.serverErrors[apiKey]) {
				return false;
			}
			return !this.$v.form[formKey].$error && null;
		},

		clearServerError(apiKey) {
			if (this.serverErrors[apiKey]) {
				this.$delete(this.serverErrors, apiKey);
			}
		},

		// parent ส่ง error ของ API กลับมา — 422 `errors: { field: [msg] }` โชว์ใต้ช่องนั้น ๆ
		setServerErrors(error) {
			const errors = pathOr({}, ['response', 'data', 'errors'])(error) || {};
			this.serverErrors = Object.keys(errors).reduce((acc, key) => ({
				...acc,
				[key]: Array.isArray(errors[key]) ? errors[key][0] : String(errors[key]),
			}), {});
		},

		handleSubmit() {
			this.$v.$touch();
			if (this.$v.$invalid) {
				return;
			}

			this.$emit('onSubmit', {
				id: this.form.id,
				isEditMode: this.isEditMode,
				params: transformedUnidayUniversityToAPI(this.form, { isEditMode: this.isEditMode }),
			});
		},
	},
};
</script>

<style lang="scss" scoped>
	.toggle-label {
		@include typo-body-2;
	}
</style>
